/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
// Extends From
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External Imports
import SharingLevel from '@linc-technologies/linced-common/enums/media-sharing-level';
import SubjectReportVisibility from '@linc-technologies/linced-common/enums/subject-report-visibility';
import config from 'linced-admin/config/environment';
import titleize from 'ember-cli-string-helpers/utils/titleize';
import { defaultTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

export default class SubjectController extends Controller {
  // Services
  @service
  fetch;

  @service
  media;

  @service
  paperToaster;

  @service
  router;

  @service
  session;

  @service
  store;

  SubjectReportVisibility = SubjectReportVisibility;

  @tracked
  gridView = true;

  isPLP = false;

  @tracked
  categoryDialogOpen = false;

  categorySaveInProgress = false;

  @tracked
  editingCategory = null;

  @tracked
  categoryLabel = '';

  @action
  setCategoryLabel(value) {
    this.categoryLabel = value;
  }

  @tracked
  categoryDescription = '';

  @action
  setCategoryDescription(value) {
    this.categoryDescription = value;
  }

  @tracked
  categoryBadgeMedia = '';

  @tracked
  categoryBadgeImage = '';

  @tracked
  subcategoryDialogOpen = false;

  subcategorySaveInProgress = false;

  @tracked
  editingSubcategory = null;

  @tracked
  subcategoryLabel = '';

  @action
  setSubcategoryLabel(value) {
    this.subcategoryLabel = value;
  }

  @tracked
  subcategoryDescription = '';

  @action
  setSubcategoryDescription(value) {
    this.subcategoryDescription = value;
  }

  @tracked
  goalDialogOpen = false;

  goalSaveInProgress = false;

  @tracked
  editingGoal = null;

  @tracked
  goalLabel = '';

  @action
  setGoalLabel(value) {
    this.goalLabel = value;
  }

  @tracked
  goalEducatorDescription = '';

  @action
  setGoalEducatorDescription(value) {
    this.goalEducatorDescription = value;
  }

  @tracked
  goalPeopleDescription = '';

  @action
  setGoalPeopleDescription(value) {
    this.goalPeopleDescription = value;
  }

  @tracked
  goalExemplar = '';

  @action
  setGoalExemplar(value) {
    this.goalExemplar = value;
  }

  @tracked
  entityToDelete = null;

  @tracked
  applyExemplarsToExistingGoals = false;

  editorOptions = Object.freeze(defaultTinyMCEConfig());

  @tracked
  uploading = false;

  @tracked
  applyCategory;

  @tracked
  applyDialogOpen;

  @tracked
  applyErrorMessage;

  @tracked
  applyInProgress;

  @tracked
  applyType;

  @tracked
  categoryBadgeDescription;

  @action
  setCategoryBadgeDescription(value) {
    this.categoryBadgeDescription = value;
  }

  @tracked
  deleteDialogOpen;

  @tracked
  deleteErrorMessage;

  @tracked
  entityTypeToDelete;

  @tracked
  goalCategory;

  @tracked
  goalSubcategory;

  @tracked
  subcategoryParent;

  @tracked
  selectedSchool;

  @action
  setSelectedSchool(value) {
    this.selectedSchool = value;
  }

  @computed(
    'model.subject.{visibleToTeachers,visibleToParents,reportVisibility}',
  )
  get showReportVisibility() {
    let { visibleToTeachers, visibleToParents, reportVisibility } =
      this.model.subject;
    return (
      (visibleToTeachers || visibleToParents) &&
      reportVisibility !== SubjectReportVisibility.NEVER
    );
  }

  @action
  addCategory() {
    this.resetCategory();
    this.categoryDialogOpen = true;
  }

  @action
  editCategory(category) {
    this.resetCategory();
    this.editingCategory = category;
    this.categoryLabel = category.get('label');
    this.categoryDescription = category.get('description');
    this.categoryBadgeMedia = category.get('badgeMedia');
    this.categoryBadgeDescription = category.get('badgeDescription');
    this.categoryDialogOpen = true;
  }

  @action
  closeCategoryDialog() {
    this.categoryDialogOpen = false;
  }

  @action
  saveCategory() {
    let category = this.editingCategory;
    let toaster = this.paperToaster;

    if (isEmpty(category)) {
      category = this.store.createRecord('goals/category', {
        schoolId: this.get('session.school'),
        subjectId: this.get('model.subject'),
        isPLP: this.isPLP,
        sequence: this.get('model.categories.length') + 1,
      });
    }

    category.set('label', this.categoryLabel);
    category.set('description', this.categoryDescription);
    category.set('badgeMedia', this.categoryBadgeMedia);
    category.set('badgeDescription', this.categoryBadgeDescription);

    category
      .save()
      .then(() => {
        this.categoryDialogOpen = false;
        if (this.isPLP) {
          // PLP goals don't have a subject, so we need to reload the categories to get new ones.
          this.get('model.categories').update();
        }
        toaster.show(`Goals category "${category.get('label')}" saved.`, {
          duration: 4000,
          position: 'top right',
        });
      })
      .catch(() => {
        // Unload a new record and revert an edited one; the form keeps the input.
        rollbackRecord(category);
        toaster.show(`Error saving goals category.`, {
          duration: 4000,
          position: 'top right',
        });
      });
  }

  @action
  addSubcategory(category) {
    this.resetSubcategory();
    this.subcategoryParent = category;
    this.subcategoryDialogOpen = true;
  }

  @action
  editSubcategory(subcategory) {
    this.resetSubcategory();
    this.editingSubcategory = subcategory;
    this.subcategoryLabel = subcategory.get('label');
    this.subcategoryDescription = subcategory.get('description');
    this.subcategoryDialogOpen = true;
  }

  @action
  closeSubcategoryDialog() {
    this.subcategoryDialogOpen = false;
  }

  @action
  saveSubcategory() {
    let subcategory = this.editingSubcategory;
    let category = this.subcategoryParent;
    let toaster = this.paperToaster;

    if (isEmpty(subcategory)) {
      subcategory = this.store.createRecord('goals/subcategory', {
        schoolId: this.get('session.school'),
        subjectId: this.get('model.subject'),
        isPLP: this.isPLP,
        categoryId: category,
        sequence: category.get('subcategories.length') + 1,
      });
    }

    subcategory.set('label', this.subcategoryLabel);
    subcategory.set('description', this.subcategoryDescription);

    subcategory
      .save()
      .then(() => {
        this.subcategoryDialogOpen = false;
        toaster.show(`Goals subcategory "${subcategory.get('label')}" saved.`, {
          duration: 4000,
          position: 'top right',
        });
      })
      .catch(() => {
        // Unload a new record and revert an edited one; the form keeps the input.
        rollbackRecord(subcategory);
        toaster.show(`Error saving goals subcategory.`, {
          duration: 4000,
          position: 'top right',
        });
      });
  }

  @action
  addGoal(category, subcategory) {
    this.resetGoal();
    this.goalCategory = category;
    this.goalSubcategory = subcategory;
    this.goalDialogOpen = true;
  }

  @action
  editGoal(goal) {
    this.resetGoal();
    this.editingGoal = goal;
    this.goalLabel = goal.get('label');
    this.goalEducatorDescription = goal.get('educatorDescription');
    this.goalPeopleDescription = goal.get('peopleDescription');
    this.goalExemplar = goal.get('exemplar');
    this.goalDialogOpen = true;
  }

  @action
  closeGoalDialog() {
    this.goalDialogOpen = false;
  }

  @action
  saveGoal() {
    let goal = this.editingGoal;
    let toaster = this.paperToaster;

    if (isEmpty(goal)) {
      let category = this.goalCategory;
      let subcategory = this.goalSubcategory;
      let sequence =
        (isEmpty(subcategory)
          ? category.get('goals.length')
          : subcategory.get('goals.length')) + 1;

      goal = this.store.createRecord('goals/goal', {
        schoolId: this.get('session.school'),
        authorId: this.get('session.person'),
        subjectId: this.get('model.subject'),
        categoryId: category,
        subcategoryId: subcategory,
        sequence,
      });
    }

    goal.set('label', this.goalLabel);
    goal.set('educatorDescription', this.goalEducatorDescription);
    goal.set('peopleDescription', this.goalPeopleDescription);
    goal.set('exemplar', this.goalExemplar);

    goal
      .save()
      .then(() => {
        this.goalDialogOpen = false;
        toaster.show(`Goal "${goal.get('label')}" saved.`, {
          duration: 4000,
          position: 'top right',
        });
      })
      .catch(() => {
        // Unload a new record and revert an edited one; the form keeps the input.
        rollbackRecord(goal);
        toaster.show(`Error saving goal.`, {
          duration: 4000,
          position: 'top right',
        });
      });
  }

  @action
  confirmDeleteEntity(type, entity) {
    this.entityTypeToDelete = type;
    this.entityToDelete = entity;
    this.deleteErrorMessage = '';
    this.deleteDialogOpen = true;
  }

  @action
  closeDeleteDialog() {
    this.deleteDialogOpen = false;
  }

  @action
  doDeleteEntity() {
    let entity = this.entityToDelete;
    let entityType = entity.get('constructor.modelName');

    if (entity) {
      entity
        .destroyRecord()
        .then(() => {
          this.deleteDialogOpen = false;
          this.paperToaster.show(
            `${titleize(this.entityTypeToDelete)} deleted successfully.`,
            {
              duration: 4000,
              position: 'top right',
            },
          );

          if (entityType === 'goals/subject') {
            // The entire subject was deleted; navigate back to the Goals admin page
            this.router.transitionTo('goals');
          }
        })
        .catch((err) => {
          this.deleteErrorMessage = err.errors[0].detail;
        });
    }
  }

  // drag-drop sorting
  @action
  sortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    const item = sourceList[sourceIndex];

    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);

    const moved = sourceList !== targetList;
    if (moved) {
      // Moved to a new parent category/subcategory - update the relationship.
      item.setProperties({
        categoryId: targetList.__category__,
        subcategoryId: targetList.__subcategory__,
      });
    }

    targetList.forEach((entity, index) => {
      entity.set('sequence', index + 1);
      let saved = entity.save();
      if (moved && entity === item) {
        // Put it back under its old category/subcategory; the lists are
        // built from the relationships, so it moves back on screen too.
        saved.catch(() => {
          rollbackRecord(entity);
          this.paperToaster.show('Error moving the goal.', {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          });
        });
      }
    });
  }

  @action
  setGridView(newState) {
    this.gridView = newState;
  }

  // Publish/unpublish entities
  @action
  togglePublish(entity) {
    entity.toggleProperty('isPublished');
    entity.save();
  }

  // Apply templates
  @action
  openApplyDialog(category) {
    if (isEmpty(category)) {
      this.applyType = 'subject';
      this.applyCategory = null;
    } else {
      this.applyType = 'category';
      this.applyCategory = category;
    }

    this.applyErrorMessage = '';
    this.applyInProgress = false;
    this.applyExemplarsToExistingGoals = false;
    this.applyDialogOpen = true;
  }

  @action
  closeApplyDialog() {
    this.applyDialogOpen = false;
  }

  @action
  applyTemplate() {
    let data = {
      schoolId: this.get('selectedSchool.id'),
      applyExemplarsToExistingGoals: this.applyExemplarsToExistingGoals,
    };
    let options = {
      data: JSON.stringify(data),
    };

    let templatesBaseUri = `${config.APP.apiHost}/goals/${config.APP.apiPath}/templates`;
    let entity = this.applyType === 'subject' ? 'subjects' : 'categories';
    let entityId =
      entity === 'subjects'
        ? this.get('model.subject.id')
        : this.get('applyCategory.id');

    this.applyInProgress = true;

    this.fetch
      .post(`${templatesBaseUri}/${entity}/${entityId}/:apply`, options)
      .then(() => {
        let toaster = this.paperToaster;
        this.applyErrorMessage = '';
        this.applyInProgress = false;
        this.applyDialogOpen = false;

        let successToast = toaster.show('Template applied successfully.', {
          duration: 8000,
          position: 'top right',
          toastClass: 'success',
          action: {
            label: 'Undo',
            onClick: () => {
              toaster.cancelToast(successToast);
              this.fetch
                .post(
                  `${templatesBaseUri}/${entity}/${entityId}/:remove`,
                  options,
                )
                .then(() => {
                  toaster.show('Template removed successfully.', {
                    duration: 4000,
                    position: 'top right',
                    toastClass: 'success',
                  });
                })
                .catch((err) => {
                  toaster.show(
                    `Error removing template: ${err.errors[0].detail}`,
                    {
                      duration: 8000,
                      position: 'top right',
                      toastClass: 'warning',
                    },
                  );
                });
            },
          },
        });
      })
      .catch((err) => {
        this.applyErrorMessage = err.errors[0].detail;
        this.applyInProgress = false;
      });
  }

  // Category badge image upload handling
  @action
  startUpload() {
    this.uploading = true;
  }

  @action
  handleUpload(file, upload) {
    this.media.handleImageUpload(
      this,
      file,
      upload,
      SharingLevel.PUBLIC,
      '',
      '',
      'categoryBadgeMedia',
      'categoryBadgeImage',
    );
  }

  @action
  handleUploadError() {
    this.paperToaster.show(`Error uploading badge image!`, {
      duration: 8000,
      position: 'top right',
      toastClass: 'warning',
    });
  }

  resetCategory() {
    this.editingCategory = null;
    this.categoryLabel = '';
    this.categoryDescription = '';
    this.categoryBadgeMedia = '';
    this.categoryBadgeImage = '';
    this.categoryBadgeDescription = '';
  }

  resetSubcategory() {
    this.editingSubcategory = null;
    this.subcategoryLabel = '';
    this.subcategoryDescription = '';
  }

  resetGoal() {
    this.editingGoal = null;
    this.goalLabel = '';
    this.goalEducatorDescription = '';
    this.goalPeopleDescription = '';
    this.goalExemplar = '';
  }
}
